import axios from "axios";
import config from "./config";
import type {
  BessResignCampaignsResponse,
  BessResignPendingResponse,
  BessResignRegenerateResponse,
  BessResignSignRequest,
  BessResignSignResponse,
} from "../types/bessResignTypes";

const BASE = `${config.API_SERVICES}/api/v1/sdp/bess-resign`;

export class BessResignError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "BessResignError";
    this.status = status;
  }
}

const toError = (err: unknown, fallback: string): BessResignError => {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status ?? 0;
    const message = err.response?.data?.message ?? err.message ?? fallback;
    return new BessResignError(status, message);
  }
  return new BessResignError(0, fallback);
};

export const fetchPendingBessResigns = async (
  companyId: number,
  token: string,
): Promise<BessResignPendingResponse> => {
  try {
    const response = await axios.get<BessResignPendingResponse>(`${BASE}/pending`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { companyId },
    });
    return response.data;
  } catch (err) {
    throw toError(err, "Failed to load pending BESS resigns");
  }
};

export const regenerateBessResignPdf = async (
  contractId: number,
  token: string,
): Promise<BessResignRegenerateResponse> => {
  try {
    const response = await axios.post<BessResignRegenerateResponse>(
      `${BASE}/regenerate`,
      { contract_id: contractId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (err) {
    throw toError(err, "Failed to regenerate BESS resign contract");
  }
};

export const fetchBessResignCampaigns = async (
  token: string,
): Promise<BessResignCampaignsResponse> => {
  try {
    const response = await axios.get<BessResignCampaignsResponse>(`${BASE}/admin/overview`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (err) {
    throw toError(err, "Failed to load BESS resign campaigns");
  }
};

export const signBessResignContract = async (
  payload: BessResignSignRequest,
  token: string,
): Promise<BessResignSignResponse> => {
  try {
    const response = await axios.post<BessResignSignResponse>(`${BASE}/sign`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (err) {
    throw toError(err, "Failed to sign BESS resign contract");
  }
};
